/**
 * useAuditLog Hook
 *
 * Provides audit log data from real Firestore auditLogs collection
 * with filtering and search.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export type AuditActionType =
  | 'role_change'
  | 'user_suspended'
  | 'user_unsuspended'
  | 'listing_paused'
  | 'listing_approved'
  | 'listing_featured'
  | 'listing_deleted'
  | 'payout_completed'
  | 'payout_rejected'
  | 'platform_config_updated'
  | 'booking_cancelled'
  | 'refund_processed';

export interface AuditLogEntry {
  id: string;
  action: AuditActionType;
  performedBy: string;
  performerName: string;
  performerEmail: string;
  targetId?: string;
  targetType?: 'user' | 'listing' | 'booking' | 'payout';
  targetName?: string;
  details: Record<string, any>;
  timestamp: Date;
  ipAddress?: string;
}

export interface AuditFilter {
  actionType?: AuditActionType | '';
  performedBy?: string;
  search?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

const toDate = (val: any): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : new Date();
};

export const useAuditLog = (filter: AuditFilter) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchLogs = async () => {
      setIsLoading(true);
      try {
        const q = query(
          collection(db, 'auditLogs'),
          orderBy('timestamp', 'desc')
        );
        const snapshot = await getDocs(q);

        let allLogs: AuditLogEntry[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            action: data.action as AuditActionType,
            performedBy: data.performedBy || '',
            performerName: data.performerName || 'Admin',
            performerEmail: data.performerEmail || '',
            targetId: data.targetId,
            targetType: data.targetType,
            targetName: data.targetName,
            details: data.details || {},
            timestamp: toDate(data.timestamp),
            ipAddress: data.ipAddress,
          };
        });

        // Apply filters
        if (filter.actionType) {
          allLogs = allLogs.filter((log) => log.action === filter.actionType);
        }
        if (filter.performedBy) {
          allLogs = allLogs.filter((log) => log.performedBy === filter.performedBy);
        }
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          allLogs = allLogs.filter(
            (log) =>
              log.performerName.toLowerCase().includes(searchLower) ||
              log.targetName?.toLowerCase().includes(searchLower) ||
              log.action.toLowerCase().includes(searchLower) ||
              JSON.stringify(log.details).toLowerCase().includes(searchLower)
          );
        }
        if (filter.dateFrom) {
          allLogs = allLogs.filter((log) => log.timestamp >= filter.dateFrom!);
        }
        if (filter.dateTo) {
          allLogs = allLogs.filter((log) => log.timestamp <= filter.dateTo!);
        }

        if (!cancelled) {
          setLogs(allLogs);
          setTotalCount(allLogs.length);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
        if (!cancelled) {
          setLogs([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    };

    fetchLogs();
    return () => { cancelled = true; };
  }, [filter.actionType, filter.performedBy, filter.search, filter.dateFrom?.getTime(), filter.dateTo?.getTime()]);

  return {
    logs,
    totalCount,
    isLoading,
  };
};

export const getActionLabel = (action: AuditActionType): string => {
  const labels: Record<AuditActionType, string> = {
    role_change: 'Role Changed',
    user_suspended: 'User Suspended',
    user_unsuspended: 'User Unsuspended',
    listing_paused: 'Listing Paused',
    listing_approved: 'Listing Approved',
    listing_featured: 'Listing Featured',
    listing_deleted: 'Listing Deleted',
    payout_completed: 'Payout Completed',
    payout_rejected: 'Payout Rejected',
    platform_config_updated: 'Config Updated',
    booking_cancelled: 'Booking Cancelled',
    refund_processed: 'Refund Processed',
  };
  return labels[action] || action;
};

export const getActionColor = (action: AuditActionType): string => {
  const colors: Record<AuditActionType, string> = {
    role_change: 'bg-blue-100 text-blue-700',
    user_suspended: 'bg-red-100 text-red-700',
    user_unsuspended: 'bg-green-100 text-green-700',
    listing_paused: 'bg-amber-100 text-amber-700',
    listing_approved: 'bg-green-100 text-green-700',
    listing_featured: 'bg-purple-100 text-purple-700',
    listing_deleted: 'bg-red-100 text-red-700',
    payout_completed: 'bg-green-100 text-green-700',
    payout_rejected: 'bg-red-100 text-red-700',
    platform_config_updated: 'bg-blue-100 text-blue-700',
    booking_cancelled: 'bg-amber-100 text-amber-700',
    refund_processed: 'bg-amber-100 text-amber-700',
  };
  return colors[action] || 'bg-gray-100 text-gray-700';
};

export default useAuditLog;
